import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Blackburn with Darwen\'s "managed decline" admission strengthen my claim?',
    answer: 'It can be relevant evidence, but no council admission guarantees success. The borough\'s May 2025 transparency report states a "managed decline strategy is in operation alongside our ideal planned and preventative maintenance model" because funding is insufficient to hold back deterioration. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on network-level policy language alone.',
  },
  {
    question: 'Blackburn has GREEN spend and best-practice scorecards — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£10.385m) matches the full DfT capital allocation. Best practice is GREEN for published asset-management alignment. Overall and condition ratings remain AMBER. Section 58 depends on inspection records and repair logs for your specific road, not aggregate scorecards.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 386km — 73% of Blackburn with Darwen\'s 529km carriageway network. The council admits 25% of roads are in poor condition "for many years" while prioritising classified arterial routes, and that most deterioration is on U-roads. At the 2024 survey, 25.28% of U-roads were red and 56.56% amber. Local roads are safety-inspected at least once a year; if your incident fell between surveys, network percentages may not capture the defect on your street.',
  },
  {
    question: 'Pothole repairs rose 91% to 13,264 in 2024/25 — does that help my case?',
    answer: 'It can. The council filled 6,946 defects in 2020/21 and 13,264 in 2024/25 — a 91% rise. The report defines these as actionable defects including potholes meeting 40mm carriageway or 25mm footway tolerances. Rising reactive volumes alongside an AMBER condition scorecard suggest defects still form across the network; that pattern does not prove your specific pothole was unavoidable, but it supports an evidence-led claim citing the council\'s own output data.',
  },
  {
    question: 'The council says red-road percentages look stable — does that weaken my claim?',
    answer: 'Not necessarily. Blackburn with Darwen explicitly warns that apparent stability in red-category roads is "due to the reactive repair of safety defects and potholes" which is "masking the overall poor and unstable condition of these roads." The council admits reactive patching means roads "are not fully restored to an acceptable standard to offer longevity or stability of repairs." If your pothole was on a repeatedly patched red or amber road, that admission is directly relevant.',
  },
  {
    question: 'What are Blackburn with Darwen\'s pothole intervention criteria?',
    answer: 'The transparency report states actionable carriageway defects require 40mm depth and footway defects 25mm depth. The potholes reporting page adds that reported defects are inspected within 10 working days using a risk-based approach, with emergency defects made safe as soon as possible — often within two hours of inspection. Meeting intervention dimensions does not automatically defeat a claim if the defect existed long enough to have been found earlier by routine inspection or prior report.',
  },
  {
    question: 'How do I report a pothole to Blackburn with Darwen Borough Council?',
    answer: 'Report potholes and surface damage via the council\'s online form at blackburn.gov.uk/roads/reporting-issues/potholes-and-surface-damage. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BlackburnWithDarwenCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Blackburn with Darwen Pothole Claims | 13,264 Repairs on Amber-Rated Roads | Fixtyer"
        description="Blackburn with Darwen earns AMBER DfT condition ratings with 13,264 pothole repairs in 2024/25 — up 91% since 2020/21 — while admitting a managed decline strategy on 386km of residential roads. See what this means for your Section 58 claim."
        canonical="/councils/blackburn-with-darwen"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Blackburn with Darwen', url: '/councils/blackburn-with-darwen' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Blackburn with Darwen Borough Council"
        councilSlug="blackburn-with-darwen"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/blackburn-with-darwen"
        headline="Blackburn with Darwen Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Blackburn with Darwen</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            13,264 Pothole Repairs in 2024/25 on an Amber-Rated Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Blackburn with Darwen earns <strong className="text-amber-400">AMBER</strong> DfT scorecards for overall
            performance and road condition, with <strong className="text-emerald-400">GREEN</strong> on spend and best
            practice. Its May 2025 transparency report records{' '}
            <strong className="text-rose-400">13,264 defect repairs in 2024/25</strong>, up 91% from 2020/21, while
            admitting a <strong className="text-amber-400">managed decline strategy</strong> on a network where{' '}
            <strong className="text-rose-400">386km of U-roads (73%)</strong> compete for maintenance against
            classified routes. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">122</div>
            <div className="text-white font-medium mt-2">Years to resurface the full network</div>
            <div className="text-brand-300 text-sm mt-1">
              At an estimated 1% annual resurfacing rate on 529km of roads — while the council admits roads are
              "deteriorating faster than we can fix them" and the resurfacing backlog grows at double the completion rate.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            529km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Blackburn with Darwen's May 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">529km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">386km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">73% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">143km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A, B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">68km A · 75km B/C combined</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">776km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footways</div>
              <div className="text-xs text-surface-500 mt-1">Plus 454km public rights of way</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Priority caveat:</strong> The council states the maintenance of "resilient and classified roads
              often takes priority" over unclassified estate roads. It is aware that 25% of roads are in poor condition
              and "have been in this condition for many years" while arterial routes are kept functional. If your
              incident was on a U-road, that documented prioritisation is structurally relevant to a Section 58 claim.
            </p>
          </div>

          <QuoteCard
            quote="We are aware there are a large number of roads (25%) in a poor condition and that they have been in this condition for many years as we attempt to keep our main arterial routes functional."
            source="Blackburn with Darwen Borough Council Local Highways Maintenance Transparency Report (May 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Annual carriageway surveys — red, amber and green grades reported to the DfT
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Survey caveat:</strong> Blackburn with Darwen surveys carriageways annually and footways
              bi-annually. Condition grades reflect the network at survey time and may not match the road on the day
              of your incident. The council warns that reactive pothole patching on red-category roads can
              "erroneously improv[e] results" and is "masking the overall poor and unstable condition of these roads."
              In 2023, 12.3% of U-roads were listed as unsurveyed.
            </p>
          </div>

          {/* Whole network */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Whole network (529km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">22.22%</td>
                    <td className="px-4 py-2 text-center">50.88%</td>
                    <td className="px-4 py-2 text-center">26.90%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">20.77%</td>
                    <td className="px-4 py-2 text-center">53.25%</td>
                    <td className="px-4 py-2 text-center">25.98%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">20.29%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">57.77%</td>
                    <td className="px-4 py-2 text-center font-bold">21.95%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Amber-condition roads rose from 50.88% in 2020 to 57.77% in 2024 while green-rated roads fell from 26.90%
              to 21.95%. Red share appears stable near 20%, but the council attributes that to reactive patching rather
              than underlying improvement.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (386km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">26.11%</td>
                    <td className="px-4 py-2 text-center">49.03%</td>
                    <td className="px-4 py-2 text-center">24.86%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">25.34%</td>
                    <td className="px-4 py-2 text-center">50.94%</td>
                    <td className="px-4 py-2 text-center">22.22%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25.28%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">56.56%</td>
                    <td className="px-4 py-2 text-center font-bold">18.17%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Roughly 98km of U-roads were in red condition and 218km in amber at the 2024 survey — about 82% of the
              residential network where the council admits deterioration is fastest and planned maintenance is
              deprioritised against classified routes.
            </p>
          </div>

          <QuoteCard
            quote="Roads repaired reactively, by filling potholes and defects as they arise, are not fully restored to an acceptable standard to offer longevity or stability of repairs."
            source="Blackburn with Darwen Borough Council Local Highways Maintenance Transparency Report (May 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend in 2025/26 — after years when reactive maintenance overtook prevention
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Blackburn with Darwen highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">10,384,986</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">10,384,986</td>
                  <td className="px-4 py-3 text-center">3,077,453</td>
                  <td className="px-4 py-3 text-center text-emerald-400">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">2,714,000</td>
                  <td className="px-4 py-3 text-center">3,462,271</td>
                  <td className="px-4 py-3 text-center">3,255,214</td>
                  <td className="px-4 py-3 text-center">52%</td>
                  <td className="px-4 py-3 text-center">48%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">3,142,000</td>
                  <td className="px-4 py-3 text-center">2,190,568</td>
                  <td className="px-4 py-3 text-center">4,481,698</td>
                  <td className="px-4 py-3 text-center">49%</td>
                  <td className="px-4 py-3 text-center">51%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">2,408,000</td>
                  <td className="px-4 py-3 text-center">2,304,754</td>
                  <td className="px-4 py-3 text-center">4,225,570</td>
                  <td className="px-4 py-3 text-center text-amber-400">47%</td>
                  <td className="px-4 py-3 text-center text-amber-400">53%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">2,408,000</td>
                  <td className="px-4 py-3 text-center">2,358,097</td>
                  <td className="px-4 py-3 text-center">3,126,525</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">3,140,000</td>
                  <td className="px-4 py-3 text-center">2,949,021</td>
                  <td className="px-4 py-3 text-center">3,012,197</td>
                  <td className="px-4 py-3 text-center text-emerald-400">62%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                DfT capital allocation jumps from £2.714m in 2024/25 to a projected £10.385m in 2025/26 — including
                £6.55m Local Transport Fund — with capital spend matching the full allocation. Preventative share is
                projected at 77%, and the council plans 21.6km of resurfacing plus 15.5km of preservation treatments
                against a historical rate of roughly 1% of the network per year.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Between 2020/21 and 2022/23, reactive maintenance exceeded prevention — hitting 53% reactive in
                2022/23 while defect repairs climbed to 13,264 in 2024/25. The council estimates £7m–£8m per year is
                needed just to hold steady or improve conditions. Forward 2025/26 investment does not retroactively
                prove the individual pothole was inspected and repaired within the council's own regime before your
                incident.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Defect Repair Volumes
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes and actionable defects corrected — carriageway and footway combined
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Blackburn with Darwen estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Defects corrected</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">6,946</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">10,467</td>
                  <td className="px-6 py-4 text-center text-amber-600">+50.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">10,282</td>
                  <td className="px-6 py-4 text-center text-amber-600">+48.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">12,722</td>
                  <td className="px-6 py-4 text-center text-amber-600">+83.2%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13,264</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+91.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="It can be widely seen on our road network that conditions are declining. Current levels of funding are insufficient to hold back deterioration and roads are deteriorating faster than we can fix them... As such, a managed decline strategy is in operation alongside our ideal planned and preventative maintenance model."
              source="Blackburn with Darwen Borough Council Local Highways Maintenance Transparency Report (May 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Roughly 36 patches a day</h3>
              <p className="text-surface-600 text-sm">
                13,264 defect corrections in 2024/25 works out to about 36 repairs per day across 529km. The council
                plans around 14,000 carriageway and footway safety defect corrections in 2025/26 while also shifting
                spend toward 21.6km of resurfacing — evidence that reactive workload and structural deterioration
                coexist even as funding rises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resurfacing maths */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Resurfacing Backlog
          </h2>
          <p className="text-surface-600 mb-8">
            Council-estimated mathematics behind the AMBER condition scorecard
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-rose-600">1%</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Network resurfaced per year</div>
              <div className="text-xs text-surface-500 mt-1">Historical estimate</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-600">122</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Years to resurface all roads</div>
              <div className="text-xs text-surface-500 mt-1">At 1% annual rate</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-600">18</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Years to clear backlog</div>
              <div className="text-xs text-surface-500 mt-1">Resurfacing queue alone</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">21.6km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Resurfacing planned 2025/26</div>
              <div className="text-xs text-surface-500 mt-1">Up from 9.66km in 2024/25</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-amber-800 text-sm">
              The council estimates asphalt design life at 15–20 years, yet at a 1% resurfacing rate most roads wait far
              longer between treatments. The backlog is "growing each year at double the rate we are undertaking
              re-surfacing schemes." Steady-state maintenance would need an estimated £7m per year; improvement plus
              steady state would need £8m. The 2025/26 capital uplift is a response — not proof past maintenance met
              a reasonable standard on every road.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Blackburn with Darwen says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>Carriageways:</strong> condition surveyed annually</li>
                <li>• <strong>Footways:</strong> surveyed bi-annually</li>
                <li>• <strong>Major roads:</strong> safety checks every 3 months</li>
                <li>• <strong>Link roads:</strong> checked every 6 months</li>
                <li>• <strong>Local roads:</strong> checked at least once a year</li>
                <li>• <strong>Reported potholes:</strong> inspected within 10 working days</li>
              </ul>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Intervention tolerances</h3>
              <p className="text-surface-600 text-sm mb-3">
                Actionable carriageway defects require 40mm depth; footway defects require 25mm depth. Emergency defects
                are made safe as soon as possible — often within two hours of inspection — using a risk-based approach.
              </p>
              <p className="text-surface-600 text-sm">
                The council's optimal model targets a 60:40 split between resurfacing and preventative treatments on
                roads, but between 2022/23 and 2024/25 it failed to meet that standard while defect volumes rose.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Blackburn with Darwen
              must maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              GREEN spend and best-practice scorecards.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the annual U-road survey — and had deterioration been recorded before your incident?</li>
              <li>• Did the defect meet 40mm/25mm thresholds during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Funding is directed to short term reactive repairs to mitigate immediate risk with planned and preventative maintenance marginalised."
            source="Blackburn with Darwen Borough Council Local Highways Maintenance Transparency Report (May 2025)"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an Amber-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: significant 2025/26 funding is arriving — but years of managed decline are documented
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Projected £10.385m capital spend matching full DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 77% preventative share projected for 2025/26 — up from 47% in 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Asset management aligned to ISO 55000 and Well-Managed Highway Infrastructure code
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full HMB incentive funding received since 2018
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing asset-management systems and the 2025/26 investment uplift.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and condition scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Published admission of managed decline alongside ideal preventative model
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 386km of U-roads (73%) where maintenance is deprioritised
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 13,264 defect repairs in 2024/25 — up 91% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits reactive patching masks poor underlying condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 25% of roads in poor condition "for many years" by the council's own account
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend scorecards and rising 2025/26 capital budgets, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond annual surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, cite 25.28% red / 56.56% amber condition and classified-road prioritisation</li>
              <li>• Whether the defect persisted through the council's own 10-working-day inspection regime after prior reports</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Blackburn with
              Darwen's own transparency data — including managed decline and masking admissions — where they help you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Blackburn with Darwen Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. The council inspects reported potholes within
            10 working days. Do this before claiming — and tell us when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.blackburn.gov.uk/roads/reporting-issues/potholes-and-surface-damage"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — blackburn.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Blackburn with Darwen?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Managed decline on the record demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No managed-decline citation</li>
                <li>• No prior-report search</li>
                <li>• No U-road prioritisation argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Managed decline admission documented</li>
                <li>✅ 13,264 defect repairs in 2024/25 cited</li>
                <li>✅ 386km U-road condition and prioritisation argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Blackburn with Darwen</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=blackburn-with-darwen"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Blackburn with Darwen Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.blackburn.gov.uk/roads/policies-and-plans/local-highway-maintenance-transparency-report-may-2025/local-highway-maintenance-transparency-report-may-2025-html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Blackburn with Darwen Borough Council Local Highways Maintenance Transparency Report (May 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
